import type {ReactNode} from "react";
import type {InsightEvidence} from "@devplaninc/codeup";
import type {EvidenceResolvers} from "@/app/components/evidence/evidenceDisplay";
import type {OpenEvidenceGroup} from "@/app/components/generative-markdown/GenerativeMarkdownBody";

export type DirectiveBody =
  | {
      type: "markdown";
      markdown: string;
    }
  | {
      type: "json";
      json: Record<string, unknown>;
    };

export type ParsedDirectiveInput = {
  name: string;
  attrs: Record<string, string>;
  body: DirectiveBody;
};

export type GenerativeMarkdownDirectiveContext = {
  evidenceById?: Record<string, InsightEvidence[]>;
  companyId?: number;
  autoOpenEvidenceId?: string;
  onOpenEvidenceGroup?: (group: OpenEvidenceGroup) => void;
  reportHref?: string;
  evidenceResolvers?: EvidenceResolvers;
  streaming?: boolean;
};

export type GenerativeMarkdownDirective = {
  name: string;
  contentType: DirectiveBody["type"];
  render: (input: ParsedDirectiveInput, context?: GenerativeMarkdownDirectiveContext) => ReactNode | undefined;
  renderEmail: (input: ParsedDirectiveInput, context?: GenerativeMarkdownDirectiveContext) => ReactNode | undefined;
};

export type DirectiveToneName = "success" | "warning" | "danger" | "info" | "neutral";

export const SUPPORTED_DIRECTIVE_TONES: ReadonlySet<DirectiveToneName> = new Set([
  "success",
  "warning",
  "danger",
  "info",
  "neutral",
]);

export function normalizeDirectiveTone(value: string | undefined): DirectiveToneName {
  const normalized = value?.trim().toLowerCase();
  if (SUPPORTED_DIRECTIVE_TONES.has(normalized as DirectiveToneName)) {
    return normalized as DirectiveToneName;
  }
  return "neutral";
}

export const directiveToneClassNames: Record<DirectiveToneName, {
  railClassName: string;
  textClassName: string;
  hoverBorderClassName: string;
}> = {
  success: {
    railClassName: "bg-emerald-600 dark:bg-emerald-400",
    textClassName: "text-emerald-700 dark:text-emerald-300",
    hoverBorderClassName: "hover:border-emerald-600/35 dark:hover:border-emerald-300/35",
  },
  warning: {
    railClassName: "bg-[#E6A437]",
    textClassName: "text-[#E6A437]",
    hoverBorderClassName: "hover:border-[#E6A437]/35",
  },
  danger: {
    railClassName: "bg-destructive",
    textClassName: "text-destructive",
    hoverBorderClassName: "hover:border-destructive/35",
  },
  info: {
    railClassName: "bg-[#1F3DFF] dark:bg-[#5A77FF]",
    textClassName: "text-[#1F3DFF] dark:text-[#5A77FF]",
    hoverBorderClassName: "hover:border-[#1F3DFF]/35 dark:hover:border-[#5A77FF]/35",
  },
  neutral: {
    railClassName: "bg-muted-foreground",
    textClassName: "text-muted-foreground",
    hoverBorderClassName: "hover:border-muted-foreground/35",
  },
};

export const directiveToneEmailStyles: Record<DirectiveToneName, {rail: string; text: string}> = {
  success: {rail: "#059669", text: "#047857"},
  warning: {rail: "#E6A437", text: "#E6A437"},
  danger: {rail: "#dc2626", text: "#b91c1c"},
  info: {rail: "#1F3DFF", text: "#1F3DFF"},
  neutral: {rail: "#6b7280", text: "#374151"},
};

export function normalizeOptionalDirectiveValue(value: string | undefined) {
  const normalized = value?.trim();
  return normalized ? normalized : undefined;
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
